import axios from 'axios';
import {
	CAR_DETAIL_IMAGE_EXCLUDED_GROUP,
	CAR_DETAIL_IMAGE_INNER_CODE,
	CAR_DETAIL_IMAGE_OPTION_CODE,
	CAR_DETAIL_IMAGE_OUTER_CODE,
} from '@encarpkg/shared';

const base = {
	quality: 0.9,
	format: 'jpeg', // png,jpg
	outputType: 'base64', // base64, canvas, blob
	width: 320,
	height: null,
	reSample: 1,
	bgColor: '#ffffff',
	saveExif: false,
};
const defaultOptions = {
	canvas: null,
	reSample: 1,
	width: 320,
	height: 240,
	cx: 0,
	cy: 0,
	cw: 0,
	ch: 0,
	dx: 0,
	dy: 0,
	dw: 0,
	dh: 0,
	bgColor: '#ffffff',
};

export const dataUrlToBlob = (b64Data, size) => {
	const sliceSize = size || 512;
	const byteCharacters = atob(b64Data.split(',')[1]);
	const contentType = b64Data.split(',')[0].split(':')[1].split(';')[0];
	const byteArrays = [];

	for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
		const slice = byteCharacters.slice(offset, offset + sliceSize);

		const byteNumbers = new Array(slice.length);
		for (let i = 0; i < slice.length; i += 1) {
			byteNumbers[i] = slice.charCodeAt(i);
		}

		const byteArray = new Uint8Array(byteNumbers);
		byteArrays.push(byteArray);
	}

	const blobData = new Blob(byteArrays, { type: contentType });
	return blobData;
};

export const urlType = (url) => {
	let type;
	if (url.indexOf('data:') !== -1) {
		type = 'dataurl';
	} else if (url.indexOf('blob:') !== -1) {
		type = 'bloburl';
	} else {
		type = 'weburl';
	}
	return type;
};

export const urlToBlob = (url, type) =>
	new Promise((resolve) => {
		const uType = type || urlType(url);
		let blobData;
		if (uType === 'dataurl') {
			blobData = dataUrlToBlob(url);
		} else {
			blobData = axios({
				method: 'get',
				url,
				responseType: 'blob',
			}).then((res) => res.data);
		}
		resolve(blobData);
	});

export function Canvas(width = 320, height = 240, bgColor = '#ffffff') {
	this.el = document.createElement('canvas');
	this.ctx = this.el.getContext('2d');

	this.el.width = width;
	this.el.height = height;

	this.ctx.fillStyle = bgColor;
	this.ctx.fillRect(0, 0, width, height);
}

export function loadImage(src) {
	return new Promise((resolve, reject) => {
		if (src) {
			const image = new Image();

			image.onload = () => {
				resolve(image);
			};
			image.onerror = (e) => {
				reject(e);
			};

			image.setAttribute('crossOrigin', 'anonymous');
			image.src = src;
		} else {
			reject(new Error('no src'));
		}
	});
}

function getFormat(str) {
	let format = null;

	switch (str) {
		case 'jpg':
		case 'jpeg':
			format = 'image/jpeg';
			break;
		case 'png':
			format = 'image/png';
			break;
		default:
			format = str;
			break;
	}

	return format;
}

/**
 * @param {HTMLCanvasElement} canvas
 */
export function base64(canvas, fmt = 'image/jpeg', quality = 1) {
	const format = getFormat(fmt);

	return new Promise((resolve, reject) => {
		try {
			const uri = canvas.toDataURL(format, quality);
			resolve(uri);
		} catch (e) {
			reject(e);
		}
	});
}

/**
 * @param {HTMLCanvasElement} canvas
 */
export function blob(canvas, fmt = 'image/jpeg', quality = 0.75) {
	const format = getFormat(fmt);

	return new Promise(async (resolve, reject) => {
		try {
			const uri = canvas.toDataURL(format, quality);
			const blobData = await urlToBlob(uri);
			resolve(blobData);
		} catch (e) {
			reject(e);
		}
	});
}

/**
 * @param {HTMLCanvasElement} parentCanvas
 */
function resize(options, count, parentCanvas) {
	return new Promise((resolve) => {
		function func(countInner, parentCanvasInner) {
			const pow = 2 ** countInner;
			const canvasForResize = new Canvas(options.width * pow, options.height * pow, options.bgColor);

			canvasForResize.ctx.drawImage(parentCanvasInner, 0, 0, parentCanvasInner.width * 0.5, parentCanvasInner.height * 0.5);

			if (countInner > 0) {
				func(countInner - 1, canvasForResize.el);
			} else {
				resolve(canvasForResize.el);
			}
		}

		func(count - 1, parentCanvas);
	});
}

export function resizeImage(opts) {
	const options = { ...defaultOptions, ...opts };
	options.reSample = Math.min(4, options.reSample);
	options.reSample = Math.max(0, options.reSample);
	const reSamplingCount = 2 ** options.reSample;

	return new Promise((resolve, reject) => {
		try {
			const canvas = new Canvas(options.width * reSamplingCount, options.height * reSamplingCount, options.bgColor);

			canvas.ctx.drawImage(
				options.canvas,
				options.cx,
				options.cy,
				options.cw,
				options.ch,
				options.dx * reSamplingCount,
				options.dy * reSamplingCount,
				options.dw * reSamplingCount,
				options.dh * reSamplingCount,
			);

			if (options.reSample > 0) {
				resize(options, options.reSample, canvas.el).then(resolve);
			} else {
				resolve(canvas.el);
			}
		} catch (e) {
			reject(e);
		}
	});
}

export function ImageResize(getOptions = {}) {
	function checkOptions(original = {}, target = {}) {
		const result = {};

		Object.keys(original).forEach((key) => {
			result[key] = target[key] || original[key];
		});

		result.width = Number(result.width);
		result.height = Number(result.height);
		result.quality = Number(result.quality);
		result.reSample = Number(result.reSample);

		return result;
	}

	this.options = checkOptions(base, getOptions);

	/**
	 * 이미지 주소로 캔버스로 변환
	 */
	function srcToCanvas(src, options) {
		let canvas = null;
		return new Promise((resolve, reject) => {
			loadImage(src).then(
				(img) => {
					canvas = new Canvas(img.width, img.height, options.bgColor);
					canvas.ctx.drawImage(img, 0, 0);
					resolve(canvas.el);
				},
				(error) => {
					reject(error);
				},
			);
		});
	}

	function blobToCanvas(el, options) {
		let canvas = null;
		return new Promise((resolve, reject) => {
			function error(e) {
				reject(e);
			}

			const reader = new FileReader();
			reader.onload = (e) => {
				const img = new Image();
				img.onload = () => {
					canvas = new Canvas(img.width, img.height, options.bgColor);
					canvas.ctx.drawImage(img, 0, 0);
					resolve(canvas.el);
				};
				img.onerror = error;
				img.src = e.target.result;
			};
			reader.onerror = error;
			reader.readAsDataURL(el);
		});
	}

	function getSize(width, height, tWidth, tHeight) {
		let w = width;
		let h = height;
		const targetWidth = tWidth;
		const targetHeight = tHeight;
		let imageType = null;

		if (width && height) {
			if (width > height) {
				imageType = 'landscape';
			} else {
				imageType = 'portrait';
			}
		}

		if (imageType === 'landscape') {
			w = targetWidth;
			h = height * (targetWidth / width);
		} else if (imageType === 'portrait') {
			w = width * (targetHeight / height);
			h = targetHeight;
		}

		return {
			width: parseInt(w, 10),
			height: parseInt(h, 10),
		};
	}

	/**
	 * 이미지 변환 실행
	 * @param {String|Blob} src
	 */
	this.play = (src) =>
		new Promise((resolve, reject) => {
			this.get(src)
				.then((canvas) => this.resize(canvas))
				.then((canvas) => this.output(canvas))
				.then((result) => resolve(result))
				.catch((error) => reject(error));
		});

	this.get = (src, opts = null) => {
		const options = opts ? checkOptions(this.options, opts) : this.options;
		return new Promise((resolve, reject) => {
			if (typeof src === 'string') {
				// image url
				resolve(srcToCanvas(src, options));
			} else if (typeof src === 'object') {
				// blob or file
				resolve(blobToCanvas(src, options));
			} else {
				reject(new Error('Not found source'));
			}
		});
	};

	/**
	 * @param {HTMLCanvasElement} canvas
	 */
	this.resize = (canvas, opts = null) => {
		const options = opts ? checkOptions(this.options, opts) : this.options;

		return new Promise((resolve, reject) => {
			const size = getSize(canvas.width, canvas.height, options.width, options.height);
			resizeImage({
				canvas,
				reSample: options.reSample,
				width: size.width,
				height: size.height,
				cx: 0,
				cy: 0,
				cw: canvas.width,
				ch: canvas.height,
				dx: 0,
				dy: 0,
				dw: size.width,
				dh: size.height,
				bgColor: options.bgColor,
			})
				.then(resolve)
				.catch(reject);
		});
	};

	/**
	 * @param {HTMLCanvasElement} canvas
	 */
	this.output = (canvas, opts = null) => {
		const options = opts ? checkOptions(this.options, opts) : this.options;

		return new Promise((resolve, reject) => {
			switch (options.outputType) {
				case 'base64':
					base64(canvas, options.format, options.quality).then(resolve).catch(reject);
					break;
				case 'blob':
					blob(canvas, options.format, options.quality).then(resolve).catch(reject);
					break;
				case 'canvas':
				default:
					resolve(canvas);
					break;
			}
		});
	};

	this.updateOptions = (value) => {
		this.options = checkOptions(this.options, value);
		return this;
	};
}

const revokeSource = (sr) => {
	const srType = typeof sr;
	if (srType === 'string' && sr.indexOf('blob:')) {
		URL.revokeObjectURL(sr);
	}
};

const loadImageSrcPromise = (src) =>
	new Promise((resolve) => {
		const image = new Image();
		image.src = src;
		image.onload = () => {
			revokeSource(src);
			resolve(image);
		};
	});

const mergePromise = (imageArray, options) =>
	new Promise((resolve) => {
		imageArray.forEach(async (image, idx) => {
			options.ctx.drawImage(image.img, image.x || 0, image.y || 0);

			if (idx + 1 === imageArray.length) {
				const dataUrl = options.canvas.toDataURL(options.format, options.quality);
				const blobData = await urlToBlob(dataUrl);
				resolve(blobData);
			}
		});
	});

export const convertBlobtoImagePromise = (sourceArray) =>
	new Promise((resolve) => {
		const promiseArray = [];
		const blobUrlArray = [];
		sourceArray.forEach((source) => {
			const fileType = typeof source;
			let result;
			if (fileType === 'string') {
				result = source;
			} else if (fileType === 'object') {
				result = URL.createObjectURL(source);
			}

			promiseArray.push(loadImageSrcPromise(result));
			blobUrlArray.push(result);
		});

		Promise.all(promiseArray).then((valueAry) => {
			resolve(valueAry);
			blobUrlArray.forEach((bu) => {
				if (bu) URL.revokeObjectURL(bu);
			});
		});
	});

export const getBase64fromImageUrl = (url, format = 'image/jpg') =>
	new Promise((resolve) => {
		const img = new Image();
		img.onload = () => {
			const canvas = document.createElement('canvas');
			const context = canvas.getContext('2d');
			canvas.width = img.width;
			canvas.height = img.height;
			context.drawImage(img, 0, 0);
			const base64Data = canvas.toDataURL(format);
			resolve(base64Data);
		};
		img.onerror = () => {
			resolve('');
		};
		img.src = url;
		img.crossOrigin = 'Anonymous';
	});

export const resizeFilePromise = (source, type, option) =>
	new Promise((resolve) => {
		const imageResize = new ImageResize({
			format: option.format,
			width: option.resizeWidth,
			height: option.resizeHeight,
			outputType: type,
			reSample: parseInt(option.resample, 10),
		});

		const resizeFile = (sr) => {
			const resizedSourcePromise = imageResize.play(sr);
			resizedSourcePromise.then((resizedSource) => {
				revokeSource(sr);
				resolve(resizedSource);
			});
		};

		resizeFile(source);
	});

export const imageMergePromise = (sources = [], option = {}) =>
	new Promise((resolve) => {
		const defaultOption = {
			format: 'image/jpeg',
			quality: 1.0,
			width: undefined,
			height: undefined,
		};
		const canvas = window.document.createElement('canvas');
		const ctx = canvas.getContext('2d');
		const options = { ...defaultOption, ...option };
		canvas.width = options.width;
		canvas.height = options.height;

		const mergedImagePromise = mergePromise(sources, {
			...options,
			ctx,
			canvas,
		});

		mergedImagePromise.then((mergedImage) => {
			sources.forEach((sr) => revokeSource(sr));
			resolve(mergedImage);
		});
	});

export const PHOTO_PLUS_CODE = [1, 2];
export const ENCAR_DIAGNOSIS_PP_CODE = 2;
export const FRONT_IMAGE_PHOTO_TYPE = '001';
export const ENCAR_DIAGNOSIS_PP_PHOTO_TYPE = '027';
export const IMAGE_UPLOADER_DEFAULT_ITEM_COUNT = 20;
export const IMAGE_UPLOADER_PHOTO_PLUS_ITEM_COUNT = 50;
export const IMAGE_UPLOADER_PHOTO_PLUS_SLOT_TYPES = [...CAR_DETAIL_IMAGE_OUTER_CODE, ...CAR_DETAIL_IMAGE_INNER_CODE, ...CAR_DETAIL_IMAGE_OPTION_CODE];
export const IMAGE_UPLOADER_DEFAULT_SLOT_TYPES = [
	'001',
	'002',
	'003',
	'004',
	'005',
	'006',
	'007',
	'008',
	'009',
	'010',
	'015',
	'016',
	'017',
	'018',
	'019',
	'020',
	'021',
	'022',
	'023',
	'024',
];

/**
 * 슬롯 순서대로 startPhotoType부터 비어 있는 photoType (순환). 모두 찼으면 startPhotoType 반환.
 * @param {string[]} photoSlotTypes
 * @param {{ current: Record<string, { get: () => unknown }> }} imageItemByTypeRef
 * @param {string} startPhotoType
 */
export function getNextEmptyPhotoType(photoSlotTypes, imageItemByTypeRef, startPhotoType) {
	const map = imageItemByTypeRef.current;
	const n = photoSlotTypes.length;
	let startIdx = photoSlotTypes.indexOf(startPhotoType);
	if (startIdx < 0) startIdx = 0;
	for (let step = 0; step < n; step += 1) {
		const i = (startIdx + step) % n;
		const pt = photoSlotTypes[i];
		const item = map[pt];
		if (item && !item.get()) {
			return pt;
		}
	}
	return photoSlotTypes[startIdx];
}

/** 슬롯 순서에서 다음 photoType (순환) */
export function getNextSlotPhotoType(photoSlotTypes, currentPhotoType) {
	const idx = photoSlotTypes.indexOf(currentPhotoType);
	if (idx < 0) return photoSlotTypes[0];
	return photoSlotTypes[(idx + 1) % photoSlotTypes.length];
}

/* 이미지업로드시 3개씩 병렬 전송 */
export const IMAGE_SAVE_UPLOAD_CONCURRENCY = 3;
export async function postImageFileConcurrency(uploadFn, options = {}) {
	const { uploadStatus } = options;
	const total = uploadFn.length;
	if (total === 0) {
		return [];
	}

	const results = new Array(total);
	let completed = 0;

	const uploadProgress = (idx_concurrency) => {
		const uploadStart = (idx) =>
			uploadFn[idx]().then((result) => {
				results[idx] = result;
				completed += 1;
				uploadStatus?.(completed, total); // 진행중 / 총 건수 return
			});
		let chain = Promise.resolve();
		for (let i = idx_concurrency; i < total; i += IMAGE_SAVE_UPLOAD_CONCURRENCY) {
			// 병렬 전송 인덱스: 1(2), 4(5), 7(8), 10(11), ...
			const idx = i;
			chain = chain.then(() => uploadStart(idx));
		}
		return chain;
	};

	const uploadProgressCount = Math.min(IMAGE_SAVE_UPLOAD_CONCURRENCY, total);
	await Promise.all(Array.from({ length: uploadProgressCount }, (_, uploadProgressIndex) => uploadProgress(uploadProgressIndex)));
	return results;
}

/* 이미지업로드시 30초 타임아웃, 실패시 2회 재시도 */
export const IMAGE_UPLOAD_TIMEOUT = 30000;
export const IMAGE_UPLOAD_RETRY_COUNT = 2;
export async function uploadRetry(uploadFn) {
	const maxAttempts = 1 + IMAGE_UPLOAD_RETRY_COUNT;

	const tryAttempt = async (remaining) => {
		try {
			return await uploadFn();
		} catch (lastError) {
			if (remaining <= 1) {
				throw lastError;
			}
			return tryAttempt(remaining - 1);
		}
	};

	return tryAttempt(maxAttempts);
}

// 진단++ 차량일때 앞측면 이미지 경로를 027로 변경함, 원본데이터는 originalData에 저장
export const diagnosis2PhotoViewArray = (photos) => {
	const item001 = photos.find((photo) => photo.photoType === FRONT_IMAGE_PHOTO_TYPE);
	const item027 = photos.find((photo) => photo.photoType === ENCAR_DIAGNOSIS_PP_PHOTO_TYPE);

	if (!item001 || !item027) return photos;
	const newPhotos = photos.filter((photo) => photo.photoType !== FRONT_IMAGE_PHOTO_TYPE);
	return [
		{
			...item001,
			photoPath: item027.photoPath,
			url: item027.url,
			originalData: item001,
		},
		...newPhotos,
	];
};

// 진단++ 차량일때 앞측면 이미지 경로를 원래의 값으로 변경함, originalData 데이터를 참조함
export const diagnosis2PhotoSaveArray = (photos) => {
	const item001 = photos.find((photo) => photo.photoType === FRONT_IMAGE_PHOTO_TYPE);

	if (!item001) return photos;
	const { originalData, ...restItem001 } = item001;
	const restoredItem001 = originalData
		? {
				...restItem001,
				photoPath: originalData.photoPath,
				url: originalData.url,
			}
		: restItem001;

	// 027로 맵핑되어있는 앞측면 데이터는 삭제
	const newPhotos = photos.filter((photo) => photo.photoType !== FRONT_IMAGE_PHOTO_TYPE);
	return [restoredItem001, ...newPhotos];
};

// 포토플러스가 아닐때 이미지 20장 (썸네일 제외)
export const imageListDefault = (items) => {
	if (!items) return [];
	return items.filter((item) => IMAGE_UPLOADER_DEFAULT_SLOT_TYPES.includes(item.photoType));
};
// 포토플러스일때 이미지 50장 (썸네일 제외)
export const imageListPhotoPlus = (items) => {
	if (!items) return [];
	return items.filter((item) => !CAR_DETAIL_IMAGE_EXCLUDED_GROUP.includes(item.photoType) && item.photoType !== ENCAR_DIAGNOSIS_PP_PHOTO_TYPE);
};
